import { useEffect, useRef } from 'react';
import MonthlyScheduler from '../Components/MonthlyScheduler';
import { RepeatType, TaskScheduleValue } from '../Components/CalendarSchedulerTypes';
import CustomDatePicker from '../Components/CustomDatePicker';
import { CustomInput, CustomSelect, CustomTextarea } from '../Components/CustomFormElements';
import '../Components/CalendarScheduler.css';
import ScheduleSummary from '../Components/ScheduleSummary';
import WorkScheduler from '../Components/WorkScheduler';
import { getRepeat, isMonthlySchedule, switchRepeat } from '../utils/calendarSchedule';
import { TaskScheduleForm as FormState, TaskScheduleFormProps } from './TaskScheduleHelpers';

const REPEAT_OPTIONS: { value: RepeatType; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

function TaskScheduleForm({
  form,
  agents,
  isDisabled = false,
  onChange,
  savedSchedule,
}: TaskScheduleFormProps) {
  const set = (partial: Partial<FormState>) => onChange({ ...form, ...partial });
  const repeat = getRepeat(form.schedule);

  const drafts = useRef<Partial<Record<RepeatType, TaskScheduleValue>>>({});
  useEffect(() => {
    drafts.current = {};
  }, [savedSchedule]);

  const changeRepeat = (next: RepeatType) => {
    if (next === repeat) return;
    drafts.current[repeat] = form.schedule;
    set({ schedule: drafts.current[next] ?? switchRepeat(form.schedule, next) });
  };

  return (
    <>
      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title mr-3">Task Schedule Name</div>
          <div className="small-font">Enter task schedule name</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomInput
            id="task-schedule-name"
            label="Task Schedule Name"
            placeholder="Enter task schedule name"
            type="text"
            value={form.name}
            onChange={(_, value) => set({ ...form, name: value })}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title mr-3">Task Definition</div>
          <div className="small-font">Enter task definition</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomTextarea
            id="task-definition"
            label="Task Definition"
            mandatory={true}
            disabled={isDisabled}
            addedText={form.taskDefinition}
            onChange={(value) => set({ taskDefinition: value })}
            resizable={false}
            height="200px"
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Agent</div>
          <div className="small-font">Select an agent</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSelect
            id="agent-select"
            label="Agent"
            options={agents.map((a) => ({
              value: a.key,
              label: a.name,
            }))}
            value={form.assistantKey}
            multiSelect={false}
            onChange={(val) => set({ ...form, assistantKey: val as string })}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Start Date</div>
          <div className="small-font">Select start date</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomDatePicker
            name="Start Date"
            value={form.startDate}
            onChange={(value) => set({ ...form, startDate: value })}
            max={form.endDate}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">End Date</div>
          <div className="small-font">Select end date</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomDatePicker
            name="End Date"
            value={form.endDate}
            onChange={(value) => set({ ...form, endDate: value })}
            min={form.startDate}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Repeat</div>
          <div className="small-font">How often it runs</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <div className="cs-segmented">
            {REPEAT_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`cs-segmented-option ${repeat === option.value ? 'cs-segmented-option--active' : ''}`}
                onClick={() => changeRepeat(option.value)}
                disabled={isDisabled}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Trigger on</div>
          <div className="small-font">Select trigger</div>
        </div>
        <div className="align-wrapper d-flex flex-column ts-trigger">
          {isMonthlySchedule(form.schedule) ? (
            <MonthlyScheduler
              schedule={form.schedule}
              setSchedule={(schedule) => set({ schedule })}
              isDisabled={isDisabled}
            />
          ) : (
            <WorkScheduler
              schedule={form.schedule}
              setSchedule={(schedule) => set({ ...form, schedule })}
              isDisabled={false}
              showEndTime={false}
            />
          )}
          <ScheduleSummary
            schedule={form.schedule}
            startDate={form.startDate}
            endDate={form.endDate}
          />
        </div>
      </div>
    </>
  );
}

export default TaskScheduleForm;
